import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import ScrollSpyNav from "@/components/site/ScrollSpyNav";
import GoldFoilFrame from "@/components/site/GoldFoilFrame";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { OG_IMAGES, SITE_URL} from "@/lib/seo";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/4x1u0vze_profile%20pic.jpg";

const SPY_ANCHORS = [
  { id: "origin", label: "Origin" },
  { id: "vision", label: "Vision" },
  { id: "technology", label: "Technology" },
  { id: "patents", label: "Patents" },
  { id: "today", label: "Today" },
];

// Build a stable React key for each story paragraph block based on its shape.
function paragraphKey(block, index) {
  if (typeof block === "string") return block.slice(0, 40);
  if (block.pull) return `pull:${block.pull.slice(0, 30)}`;
  if (block.closing) return `closing:${block.closing.slice(0, 30)}`;
  if (block.image) return `image:${block.image.src}`;
  if (block.anchor) return `anchor:${block.anchor}`;
  return `block-${index}`;
}

const PARAGRAPHS = [
  { anchor: "origin" },
  "Integrated Wellness was born from something deeply personal.",
  "Founder John Campetella watched his parents struggle through the limitations of traditional healthcare — a system that often treated symptoms separately, failed to connect the bigger physiological picture, and too frequently left people searching for answers without truly understanding the root cause of what was happening inside the body.",
  "Watching that experience firsthand became the driving force behind his life's work.",
  "Determined to understand human health at a deeper level, John dedicated himself to studying the sciences that form the foundation of medicine and human physiology. He earned a B.A. in Biology and an M.A. in Biological Education before going on to serve as an adjunct and associate professor teaching anatomy and physiology, human biology, microbiology, pharmacology, chemistry, and physics.",
  "But John's education extended far beyond the classroom.",
  "As a lifelong athlete, coach, and former UFC competitor, he spent decades immersed in the practical application of human performance, recovery, metabolism, conditioning, injury adaptation, and physiological stress. Combining academic science with real-world experience gave him a unique perspective on how the body actually functions under pressure, adaptation, and recovery.",
  {
    image: {
      src: "/team/john-ufc.jpg",
      alt: "John Campetella, former UFC competitor — decades of real-world physiology under pressure, adaptation, and recovery",
      caption: "Former UFC competitor — decades of real-world physiology under pressure, adaptation, and recovery.",
      ratio: "16/9",
    },
  },
  "Through decades of teaching, coaching, competing as a former UFC athlete, and observing critical medicine firsthand, John recognized a major flaw in modern healthcare: the body was being treated in fragments when it actually functions as one interconnected system.",
  { anchor: "vision" },
  "Rather than viewing metabolism, hormones, inflammation, cardiovascular health, neurology, recovery, sleep, stress, gut health, and performance as isolated specialties, John saw them as deeply connected physiological systems constantly influencing one another.",
  {
    pull: "That realization led him to develop the Unified Medical Model — a systems-based framework built upon the seven core fundamental sciences and centered on the concept that the human body must be understood through its interconnected relationships rather than through isolated symptom management alone.",
  },
  { anchor: "technology" },
  "Driven by the belief that better systems could fundamentally transform healthcare outcomes, John taught himself software architecture, AI integration, automation systems, and computational modeling in order to build the technology himself from the ground up.",
  "The result became the Integrated Matrix Engine — a proprietary clinical intelligence framework designed to integrate symptom mapping, biomarker analysis, physiological ratios, pattern recognition, feedback loops, longitudinal health tracking, and cross-domain physiological analysis into one unified system.",
  "What makes the achievement particularly remarkable is that much of the framework, architecture, and conceptual design was independently developed by one individual pursuing challenges that even billion-dollar healthcare and technology companies have struggled to solve.",
  "John's work now extends beyond health optimization and into the future of diagnostics itself. His ongoing research and patent applications include advanced diagnostic systems utilizing mobile technology, AI-assisted physiological analysis, and wave-model applications applied to EEG and EKG interpretation. By applying interconnected wave and tensor-based analysis to neurological and cardiac diagnostics, his work aims to expand how artificial intelligence can be used not only in medicine, but in the future of human diagnostics and performance optimization.",
  { anchor: "patents" },
  {
    patents: {
      title: "Patent-Pending Portfolio",
      eyebrow: "Five Filings · Patent Pending",
      items: [
        {
          name: "Integrated Biological Age (IBA) 2.0",
          sub: "Weighted-ensemble biological-age engine fusing epigenetic, vascular, and behavioral signals with a Biological Resilience Reserve — distinguishing Resilient from Fragile aging.",
        },
        {
          name: "Wave-Harmonic Model — CardioSentry ICIT",
          sub: "P-QRS-T-U coupling analysis for real-time Wave-Harmonic stability scoring — the engine behind treating exercise as a structural drug.",
        },
        {
          name: "Multi-Tissue Biometric Engine",
          sub: "Multi-layer signal fusion across skin, muscle, nerves, and vessels for real-time, non-invasive whole-body biometrics.",
        },
        {
          name: "Multi-Tissue Photometric Engine",
          sub: "Non-invasive multi-wavelength light-based reading of tissue-depth-specific physiological signals — energy, inflammation, hydration, oxygenation.",
        },
        {
          name: "Body Composition & Metabolic Risk Engine",
          sub: "Visceral fat, muscle quality, metabolic age, sarcopenia and cardiometabolic risk — fused into one longitudinal risk signal.",
        },
      ],
    },
  },
  "At its core, Integrated Wellness was never built simply as another wellness company.",
  { anchor: "today" },
  {
    image: {
      src:
        "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/4x1u0vze_profile%20pic.jpg",
      alt: "John Campetella — founder of Integrated Clinical Intelligence (face portrait)",
      caption: "Today — building the operating system for whole-human medicine.",
      ratio: "1/1",
      align: "right",
      face: true,
    },
  },
  {
    closing: "It was built from a son's determination to find better answers for his parents — and from a lifelong pursuit to create a more intelligent, interconnected, and human-centered model of healthcare.",
  },
];

function Paragraph({ block, index }) {
  if (typeof block === "string") {
    return (
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, delay: 0.04 * (index % 4) }}
        className="text-lg md:text-xl text-foreground/85 leading-relaxed mt-7 first:mt-0"
      >
        {block}
      </motion.p>
    );
  }
  if (block.anchor) {
    // Invisible scroll-target — gives sections IDs without breaking the prose flow.
    return <div id={block.anchor} aria-hidden className="scroll-mt-28 h-0" />;
  }
  if (block.pull) {
    return (
      <motion.blockquote
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="my-12 md:my-16 border-l-2 border-primary pl-6 md:pl-8
                   font-display text-2xl md:text-3xl font-semibold leading-snug
                   text-foreground/90"
      >
        {block.pull}
      </motion.blockquote>
    );
  }
  if (block.image) {
    const {
      src,
      alt,
      caption,
      ratio = "16/9",
      align,
      face,
      objectPosition,
    } = block.image;
    return (
      <motion.figure
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className={
          align === "right"
            ? "my-12 md:my-16 mx-auto md:mr-0 md:ml-auto md:max-w-sm"
            : "my-12 md:my-16"
        }
      >
        <div
          className="relative rounded-2xl overflow-hidden border border-border shadow-lg bg-foreground"
          style={{ aspectRatio: ratio }}
        >
          <img
            src={src}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            style={
              face
                ? {
                    objectPosition: objectPosition || "50% 30%",
                    transform: "scale(2.4)",
                    transformOrigin: "50% 30%",
                  }
                : objectPosition
                ? { objectPosition }
                : undefined
            }
          />
        </div>
        {caption && (
          <figcaption className="mt-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-foreground/55">
            {caption}
          </figcaption>
        )}
      </motion.figure>
    );
  }
  if (block.closing) {
    return (
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-10 font-display text-2xl md:text-3xl font-semibold leading-snug
                   text-primary"
      >
        {block.closing}
      </motion.p>
    );
  }
  if (block.patents) {
    const { title, eyebrow, items } = block.patents;
    return (
      <motion.section
        data-testid="founder-patent-portfolio"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.65 }}
        className="my-12 md:my-16 rounded-3xl border-2 border-accent/35 bg-card p-7 md:p-10"
      >
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-accent mb-3">
          {eyebrow}
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
          {title}
        </h3>
        <ol className="mt-7 space-y-5">
          {items.map((item, i) => (
            <li
              key={item.name}
              data-testid={`founder-patent-${i + 1}`}
              className="grid grid-cols-[auto_1fr] gap-4 md:gap-5 pb-5 border-b border-border last:border-b-0 last:pb-0"
            >
              <span className="font-mono text-xs font-bold text-accent pt-1">
                0{i + 1}
              </span>
              <div>
                <div className="font-display text-lg md:text-xl font-semibold leading-snug text-foreground">
                  {item.name}
                </div>
                <p className="mt-1.5 text-base text-foreground/70 leading-relaxed">
                  {item.sub}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </motion.section>
    );
  }
  return null;
}

export default function FounderStory() {
  useDocumentMeta({
    title: "Founder's Story — John Campetella, Founder & CEO | Integrated Clinical Intelligence",
    description:
      "John Campetella — founder, technologist, and former UFC competitor — built Integrated Clinical Intelligence to replace fragmented specialist care with one unified AI platform backed by a licensed medical team. Patent-pending.",
    image: OG_IMAGES.founderStory,
    url: `${SITE_URL}/founder-story`,
  });

  return (
    <div data-testid="founder-story-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <ScrollSpyNav anchors={SPY_ANCHORS} />
      <main className="pt-32 md:pt-36 pb-20 md:pb-28">
        {/* Top — eyebrow / title */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Link
            to="/"
            data-testid="founder-back-link"
            className="eyebrow inline-flex items-center gap-2 text-foreground/65 hover:text-primary transition-colors"
          >
            <span aria-hidden>←</span> Back to Integrated Clinical Intelligence
          </Link>
          <div className="mt-8 iw-overline">
            Founder's Story
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display font-bold tracking-[-0.03em] leading-[0.98]
                       text-[clamp(2.75rem,6.5vw,5.5rem)]"
          >
            Why I built <em className="not-italic text-primary">Integrated Wellness</em>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-7 text-lg md:text-xl text-foreground/75 leading-relaxed max-w-2xl mx-auto"
          >
            The story behind the Unified Medical Model and the Integrated Matrix Engine —
            in the words of founder <strong className="text-foreground">John Campetella</strong>.
          </motion.p>
        </section>

        {/* Hero portrait — gold-foil animated frame */}
        <section className="max-w-2xl mx-auto px-4 md:px-8 mt-14 md:mt-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
            className="max-w-sm md:max-w-md mx-auto block"
          >
            <GoldFoilFrame rounded="3xl" inset={3} sheen className="w-full">
              <div className="relative aspect-square rounded-3xl overflow-hidden bg-foreground w-full">
                <img
                  src={HERO_IMG}
                  alt="John Campetella, founder of Integrated Clinical Intelligence — the story behind the Unified Medical Model"
                  className="w-full h-full object-cover object-center"
                  loading="eager"
                  style={{ filter: "brightness(1.03) contrast(1.04) saturate(1.05)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/25 via-transparent to-transparent pointer-events-none" />
              </div>
            </GoldFoilFrame>
          </motion.div>
          <p className="mt-5 text-center text-sm md:text-base text-foreground/60 font-mono uppercase tracking-[0.22em]">
            John Campetella · Founder &amp; CEO
          </p>
        </section>

        {/* Long-form story */}
        <article className="max-w-3xl mx-auto px-4 md:px-8 mt-16 md:mt-20">
          {PARAGRAPHS.map((block, i) => (
            <Paragraph key={paragraphKey(block, i)} block={block} index={i} />
          ))}
          <p className="mt-12 font-mono text-xs md:text-sm uppercase tracking-[0.28em] text-primary">
            — John Campetella, Founder / CEO
          </p>
        </article>

        {/* Closing CTA back to site */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 mt-20 md:mt-28 text-center">
          <Link
            to="/contact"
            data-testid="founder-contact-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full
                       bg-primary text-primary-foreground hover:bg-primary/90
                       font-semibold text-sm md:text-base
                       hover:gap-3 transition-all shadow-lg"
          >
            Start My Health Assessment <span aria-hidden className="text-base">→</span>
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
